import {
  AssetMetadata,
  AssetOwnerCategory,
  AssetResponse,
  AssetType
} from 'shared/models/Asset';
import {
  LessonContextConsumer,
  useLessonContext
} from '../../../content/models/LessonContext';
import React, { useEffect, useState } from 'react';

import { AssetContentView } from '../../../shared/components/AssetContentView';
import AssetService from '../../../shared/services/AssetService';
import { AssetUploader } from '../../../shared/components/AssetUploader';
import Box from '@mui/material/Box';
import { CustomLoadingProgress } from '../../../shared/components/CustomLoadingProgress';
import { FileUploaderComponent } from '../../models/LessonComponent';
import Grid from '@mui/material/Unstable_Grid2';
import componentStyles from '../../styles/LessonComponents.module.css';
import useAssets from '../../../shared/hooks/useAssets';

interface FileUploaderProps {
  component: FileUploaderComponent;
  category: AssetOwnerCategory;
  onSubmit: (value: string) => void;
}

export function FileUploader(props: FileUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [assetMetadataObj, setAssetMetadataObj] = useState<AssetMetadata>(null);
  const [assetUrl, setAssetUrl] = useState('');
  const { mode } = useLessonContext();
  const fileTypes: string[] =
    props.component?.type === 'video_uploader'
      ? ['video']
      : props.component.settings?.fileType || [];

  const getTypes = () => {
    const acceptTypes: AssetType[] = [];

    if (fileTypes.includes('image')) {
      acceptTypes.push('image');
    }
    if (fileTypes.includes('video')) {
      acceptTypes.push('video');
    }
    if (fileTypes.includes('audio')) {
      acceptTypes.push('audio');
    }
    if (fileTypes.includes('document')) {
      acceptTypes.push('document');
    }
    return acceptTypes;
  };

  const { getAssetMetadata, assetMetadataLoading, assetMetadata } = useAssets(
    getTypes(),
    'learner',
    false /* no need to load assets */
  );

  useEffect(() => {
    if (props.component.answer && mode !== 'preview') {
      const answer: AssetResponse = JSON.parse(props.component.answer);
      getAssetMetadata(
        'learner_assets',
        answer.assetkey,
        `${answer.owner}/${answer.assetkey}`
      );
    }
  }, [props.component.answer]);

  useEffect(() => {
    if (assetMetadata) {
      setAssetMetadataObj(assetMetadata);
    }
  }, [assetMetadata]);

  useEffect(() => {
    if (assetMetadataObj) {
      getAssetUrl(assetMetadataObj);
    }
  }, [assetMetadataObj]);

  const handleUploadStart = () => {
    setUploading(true);
  };

  const handleUploadCompleted = (assetMetadata: AssetMetadata) => {
    setUploading(false);
    if (assetMetadata === null) {
      //* Delete answer asset from database
      props.onSubmit(null);
      setAssetMetadataObj(null);
    } else {
      const { assetkey, owner, contentType, filename, size } = assetMetadata;
      const response: AssetResponse = {
        assetkey,
        owner,
        contentType
      };
      props.onSubmit(JSON.stringify(response));
      setAssetMetadataObj(assetMetadata);
    }
  };

  const getAssetUrl = async (assetMetadata: AssetMetadata) => {
    if (assetMetadata === null) {
      return;
    }
    const { assetkey, owner } = assetMetadata;
    const url =
      props.category === 'author'
        ? AssetService.getAssetUrl(assetkey)
        : await AssetService.getLearnerAssetUrl(owner, assetkey);
    setAssetUrl(url);
  };

  return (
    <LessonContextConsumer>
      {({ mode, quizLocked, assessmentSubmitted }) => (
        <Box mt={2} className={componentStyles.textOverflow}>
          <Grid container direction="column" alignItems="center" spacing={2}>
            {mode !== 'edit' &&
              assetMetadataObj &&
              assetUrl &&
              !uploading &&
              !assetMetadataLoading && (
                <Grid style={{ width: '100%' }}>
                  <AssetContentView
                    assetUrl={assetUrl}
                    assetMetadata={assetMetadataObj}
                  />
                </Grid>
              )}
            {assetMetadataLoading || (assetMetadataObj && !assetUrl) ? (
              <Grid>
                <CustomLoadingProgress size={40} />
              </Grid>
            ) : (
              (mode !== 'mark' || (!quizLocked && !assessmentSubmitted)) && (
                <Grid style={{ width: '100%' }}>
                  <AssetUploader
                    types={getTypes()}
                    category="learner"
                    assetMetadata={assetMetadataObj}
                    onStart={handleUploadStart}
                    onCompleted={handleUploadCompleted}
                  />
                </Grid>
              )
            )}
          </Grid>
        </Box>
      )}
    </LessonContextConsumer>
  );
}
